import { useTheme, alpha, darken, lighten, getContrastRatio, getLuminance } from '@mui/material/styles';
import { useState, useEffect } from 'react';

export default function useHeaderTheme() {
  const theme = useTheme();
  const [headerColor, setHeaderColor] = useState(undefined);
  const [iconColor, setIconColor] = useState('text.primary');
  const [hoverColor, setHoverColor] = useState('secondary.lighter');
  const [focusColor, setFocusColor] = useState('secondary.dark');
  const [badgeBg, setBadgeBg] = useState(undefined);
  const [badgeColor, setBadgeColor] = useState(undefined);
  const [badgeClash, setBadgeClash] = useState(false);
  const [headerLogo, setHeaderLogo] = useState(undefined);
  const [isLightHeader, setIsLightHeader] = useState(false);

  useEffect(() => {
    const updateTheme = () => {
      try {
        const rawAppSetting = localStorage.getItem('appSetting');
        if (rawAppSetting) {
          const appSetting = JSON.parse(rawAppSetting);

          if (appSetting?.header?.color && appSetting.header.color !== headerColor) {
            setHeaderColor(appSetting.header.color);
            try {
              const color = appSetting.header.color.toLowerCase();
              if (color === '#ffffff' || color === '#fff' || color === 'white') {
                setIconColor(theme.palette.grey[600]);
                setHoverColor(alpha(theme.palette.text.secondary, 0.08));
                setFocusColor(theme.palette.text.secondary);
                setBadgeBg(theme.palette.error.main);
                setBadgeColor(theme.palette.error.contrastText);
                setBadgeClash(false);
                setIsLightHeader(true);
              } else {
                const contrastText = theme.palette.getContrastText(appSetting.header.color);
                const isLightText = contrastText === '#fff' || contrastText === '#ffffff' || contrastText === theme.palette.common?.white;

                if (isLightText) {
                  const smartIconColor = lighten(appSetting.header.color, 0.85);
                  setIconColor(smartIconColor);
                  setHoverColor(alpha(smartIconColor, 0.15));
                  setFocusColor(alpha(smartIconColor, 0.3));
                } else {
                  const smartIconColor = darken(appSetting.header.color, 0.65);
                  setIconColor(smartIconColor);
                  setHoverColor(alpha(smartIconColor, 0.08));
                  setFocusColor(alpha(smartIconColor, 0.2));
                }

                const luminance = getLuminance(appSetting.header.color);
                setIsLightHeader(luminance > 0.45);

                setBadgeBg(theme.palette.error.main);
                setBadgeColor(theme.palette.error.contrastText);

                const errorContrast = getContrastRatio(theme.palette.error.main, appSetting.header.color);
                if (errorContrast < 2.5) {
                  setBadgeClash(true);
                } else {
                  setBadgeClash(false);
                }
              }
            } catch {
              setIconColor(theme.palette.grey[600]);
              setHoverColor('secondary.lighter');
              setFocusColor('secondary.dark');
              setBadgeBg(theme.palette.error.main);
              setBadgeColor(theme.palette.error.contrastText);
              setBadgeClash(false);
              setIsLightHeader(true);
            }
          }
          if (appSetting?.header?.logo) {
            // Gunakan modified_date sebagai cache buster agar browser mengambil gambar terbaru (bypass cache)
            const modifiedTime = appSetting.modified_date ? new Date(appSetting.modified_date).getTime() : '';
            const logoUrl = appSetting.header.logo;
            const newLogoUrl = modifiedTime ? `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}v=${modifiedTime}` : logoUrl;

            if (newLogoUrl !== headerLogo) {
              setHeaderLogo(newLogoUrl);
            }
          }
        }
      } catch (e) {
        console.error('Failed to parse appSetting from localStorage', e);
      }
    };

    updateTheme();
    const interval = setInterval(updateTheme, 1000);
    return () => clearInterval(interval);
  }, [theme, headerColor, headerLogo]);

  return { headerColor, iconColor, headerLogo, hoverColor, focusColor, badgeBg, badgeColor, badgeClash, isLightHeader };
}
